import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import axios from "axios";

// ── Styles ─────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "100%",
    maxWidth: "900px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    padding: "12px 16px",
    textAlign: "left",
    verticalAlign: "middle",
    fontSize: "0.875rem",
    color: "#333",
  },
  checkNameWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  apiCell: {
    color: "#555",
  },
  timeWindowInput: {
    width: "100px",
    "& input": {
      padding: "6px 8px",
      height: "30px",
      textAlign: "center",
      fontSize: "0.875rem",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      "& fieldset": {
        borderColor: "#ddd",
      },
      "&:hover fieldset": {
        borderColor: "#1890ff",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1890ff",
      },
    },
  },
  timeWindowInputError: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: "#f44336",
    },
  },
  validationError: {
    color: "#f44336",
    fontSize: "0.7rem",
    marginTop: "2px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "90%",
    marginTop: theme.spacing(4),
  },
  backButton: {
    backgroundColor: "#707070",
    color: "#fff",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#4d4d4d",
    },
  },
  saveButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#993d13",
    },
  },
  statusMessage: {
    fontSize: "0.875rem",
    fontWeight: 500,
    marginTop: "8px",
  },
}));

// ── Constants ──────────────────────────────────────────────────────────────
const MIN_TIME_WINDOW = 1;
const MAX_TIME_WINDOW = 10080; // 7 days in minutes

// ── Component ──────────────────────────────────────────────────────────────
const RecheckTimeWindowTab = (props) => {
  const {
    recheckTimeWindowConfig,
    setRecheckTimeWindowRefresh,
    loading,
    error,
    handleBack,
    sponsorHandleNext,
    networkUrl,
    network,
    addSponsorList,
    rowData,
  } = props;

  const classes = useStyles();
  // const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const [configs, setConfigs] = useState([]);
  const [originalConfigs, setOriginalConfigs] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState({ text: "", type: "" });
  const [editingIndex, setEditingIndex] = useState(null);

  // ── Sync incoming prop to local state ────────────────────────────────────
  useEffect(() => {
    if (recheckTimeWindowConfig && recheckTimeWindowConfig.length > 0) {
      setConfigs(JSON.parse(JSON.stringify(recheckTimeWindowConfig)));
      setOriginalConfigs(JSON.parse(JSON.stringify(recheckTimeWindowConfig)));
      setIsUpdated(false);
      setValidationErrors({});
    }
  }, [recheckTimeWindowConfig]);

  // ── Auto-clear status message after 4 seconds ────────────────────────────
  useEffect(() => {
    if (!statusMessage.text) return;
    const timer = setTimeout(
      () => setStatusMessage({ text: "", type: "" }),
      4000
    );
    return () => clearTimeout(timer);
  }, [statusMessage.text]);

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = useCallback((value) => {
    if (value === "" || value === null || value === undefined) {
      return "Required";
    }
    const n = Number(value);
    if (!Number.isInteger(n)) return "Must be a whole number";
    if (n < MIN_TIME_WINDOW) return `Min ${MIN_TIME_WINDOW}`;
    if (n > MAX_TIME_WINDOW) return `Max ${MAX_TIME_WINDOW}`;
    return null;
  }, []);

  // ── Handle Change ────────────────────────────────────────────────────────
  const handleTimeWindowChange = useCallback(
    (index, rawValue) => {
      if (rawValue !== "" && !/^\d*$/.test(rawValue)) return;

      const updated = [...configs];
      updated[index] = {
        ...updated[index],
        timeWindowMinutes: rawValue === "" ? "" : parseInt(rawValue, 10),
      };
      setConfigs(updated);

      const err = validate(rawValue === "" ? "" : parseInt(rawValue, 10));
      setValidationErrors((prev) => ({
        ...prev,
        [updated[index].recheckConfigID]: err,
      }));

      const changed = updated.some(
        (c, i) => c.timeWindowMinutes !== originalConfigs[i]?.timeWindowMinutes
      );
      setIsUpdated(changed);
      setStatusMessage({ text: "", type: "" });
    },
    [configs, originalConfigs, validate]
  );

  // ── Save & Next ──────────────────────────────────────────────────────────
  const handleNextClick = useCallback(async () => {
    const errors = {};
    let hasErr = false;
    configs.forEach((c) => {
      const e = validate(c.timeWindowMinutes);
      if (e) {
        errors[c.recheckConfigID] = e;
        hasErr = true;
      }
    });
    setValidationErrors(errors);
    if (hasErr) return;

    setSaving(true);
    setStatusMessage({ text: "", type: "" });

    try {
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateRecheckTimeWindowConfig`;
      const payload = {
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        recheckConfigList: configs.map((c) => ({
          recheckConfigID: c.recheckConfigID,
          checkName: c.checkName,
          checkKey: c.checkKey,
          apiDescription: c.apiDescription,
          timeWindowMinutes: c.timeWindowMinutes,
        })),
      };
      const response = await axios.post(url, payload);
      if (!(response.data?.status === true || response.data?.status === "true")) {
        setStatusMessage({
          text: response.data?.message || "Failed to save. Please try again.",
          type: "error",
        });
        return;
      }

      setOriginalConfigs(JSON.parse(JSON.stringify(configs)));
      setIsUpdated(false);

      if (setRecheckTimeWindowRefresh) {
        setRecheckTimeWindowRefresh(true);
      }

      if (sponsorHandleNext) {
        sponsorHandleNext();
      }
    } catch (err) {
      console.error("Error saving recheck time window config:", err);
      setStatusMessage({
        text: "Failed to save. Please try again.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [configs, validate, setRecheckTimeWindowRefresh, sponsorHandleNext, networkUrl, network, addSponsorList, rowData]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={classes.container}>
      <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Check</TableCell>
              <TableCell className={classes.headerCell}>API</TableCell>
              <TableCell className={classes.headerCell}>
                Time Window (in Minutes)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography>{error}</Typography>
                </TableCell>
              </TableRow>
            ) : configs.map((config, index) => (
              <TableRow key={config.recheckConfigID}>
                {/* Check Name */}
                <TableCell className={classes.bodyCell}>
                  <div className={classes.checkNameWrapper}>
                    <span>{config.checkName}</span>
                  </div>
                </TableCell>

                {/* API Description */}
                <TableCell
                  className={`${classes.bodyCell} ${classes.apiCell}`}
                >
                  {config.apiDescription}
                </TableCell>

                {/* Time Window — text + pencil, or editable TextField */}
                <TableCell className={classes.bodyCell}>
                  {editingIndex === index ? (
                    <>
                      <TextField
                        variant="outlined"
                        size="small"
                        type="text"
                        autoFocus
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        value={
                          config.timeWindowMinutes === ""
                            ? ""
                            : config.timeWindowMinutes
                        }
                        onChange={(e) =>
                          handleTimeWindowChange(index, e.target.value)
                        }
                        onBlur={() => setEditingIndex(null)}
                        disabled={saving}
                        className={`${classes.timeWindowInput} ${
                          validationErrors[config.recheckConfigID]
                            ? classes.timeWindowInputError
                            : ""
                        }`}
                        error={!!validationErrors[config.recheckConfigID]}
                      />
                      {validationErrors[config.recheckConfigID] && (
                        <div className={classes.validationError}>
                          {validationErrors[config.recheckConfigID]}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ display: "flex", alignItems: "center" }}>
                      {config.timeWindowMinutes}
                        <IconButton
                          size="small"
                          onClick={() => setEditingIndex(index)}
                          style={{ marginLeft: 4 }}
                          disabled={saving}
                        >
                          <EditIcon style={{ color: "orange" }} />
                        </IconButton>
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

      {/* Status message */}
      {statusMessage.text && (
        <div
          className={classes.statusMessage}
          style={{
            color: statusMessage.type === "success" ? "#4caf50" : "#f44336",
          }}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Footer */}
      <div className={classes.footer}>
        <Grid
          className={classes.backButton}
          onClick={() => { if (handleBack) handleBack(); }}
        >
          <ChevronLeftIcon />
          Back
        </Grid>
        <Grid
          className={classes.saveButton}
          onClick={() => {
            if (isUpdated) {
              handleNextClick();
            } else if (sponsorHandleNext) {
              sponsorHandleNext();
            }
          }}
          style={{ opacity: saving ? 0.7 : 1, pointerEvents: saving ? "none" : "auto" }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: "8px" }} />
              Saving...
            </>
          ) : (
            <>
              Save & Next
              <ChevronRightIcon />
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default RecheckTimeWindowTab;
